/**
 * Copy and time helpers for an enrollment request awaiting its teacher
 * (D-P07-03, CTO 12 Oct 2026: Stripe manual capture). One place, so the
 * parent's and the teacher's screens describe the same request the same way
 * (PRT-BR-P07-10: two projections of one intent).
 */

import type { EnrollmentState, TeacherDeclineReason } from '../api/enrollments';
import type { DisplayTimeZone } from '../api/learner';
import { formatShortDateTime } from '../time/zonedTime';

// Constants live here, not in the API module: tests replace that module
// wholesale, and a screen must not lose its rules when they do.

/**
 * States that hold the seat for a (parent, child, course) — the same list the
 * server uses for duplicate detection. A request awaiting the teacher holds it:
 * the family's card is authorized for it.
 */
export const OCCUPYING_ENROLLMENT_STATES: readonly EnrollmentState[] = [
  'PendingPayment',
  'PendingTeacherAcceptance',
  'Active',
];

export const DECLINE_REASON_LABELS: Record<TeacherDeclineReason, string> = {
  SCHEDULE_CONFLICT: 'Conflicto de horario',
  CLASS_NOT_VIABLE: 'La clase no es viable',
  LEARNER_NOT_A_FIT: 'El curso no es adecuado para este alumno',
  CAPACITY_CHANGED: 'Cambió el cupo disponible',
  TEACHER_UNAVAILABLE: 'No estoy disponible',
};

/** How each enrollment state reads to a family, in one place. */
export const ENROLLMENT_STATE_LABEL: Record<EnrollmentState, string> = {
  PendingPayment: 'Pago pendiente',
  PendingTeacherAcceptance: 'Esperando al profesor',
  Active: 'Activa',
  PaymentFailed: 'Pago rechazado',
  TeacherDeclined: 'No aceptada',
  AcceptanceExpired: 'Vencida',
  Cancelled: 'Cancelada',
  Completed: 'Completada',
};

/**
 * The deadline as a person reads it: "lun 13 oct, 08:00". With `tz`, in the
 * zone the server chose for the family (UC-P08 BR-P08-04); the teacher's
 * screens do not pass one yet.
 */
export function formatDeadline(iso?: string, tz?: DisplayTimeZone): string {
  if (!iso) return 'por confirmar';
  if (tz) return formatShortDateTime(iso, tz);
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-MX', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Time left before the window closes, from a server-supplied `now` so a
 * wrong device clock cannot show a teacher more time than they have.
 */
export function timeLeftLabel(acceptBy: string | undefined, now: Date): string {
  if (!acceptBy) return 'Sin plazo registrado';
  const ms = Date.parse(acceptBy) - now.getTime();
  if (Number.isNaN(ms)) return 'Sin plazo registrado';
  if (ms <= 0) return 'Plazo vencido';
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `Quedan ${minutes} min`;
  return `Quedan ${hours} h ${minutes} min`;
}

/** What a parent is told about each outcome of the acceptance gate. */
export const REQUEST_OUTCOME_COPY = {
  awaiting:
    'Tu tarjeta está autorizada, pero todavía no se ha cobrado nada. Si el profesor acepta, ' +
    'se realiza el cobro y la inscripción se activa. Si no acepta o no responde a tiempo, la ' +
    'autorización se libera y no se cobra nada.',
  declined:
    'El profesor no pudo aceptar esta solicitud. No se realizó ningún cargo y la autorización de ' +
    'tu tarjeta se liberó.',
  expired:
    'El profesor no respondió a tiempo. No se realizó ningún cargo y la autorización de tu ' +
    'tarjeta se liberó.',
} as const;
